import { createTheme, Theme } from "@mui/material/styles";
import { TenantTokens } from "../tokens/types";

export function createTenantTheme(tokens: TenantTokens): Theme {
  return createTheme({
    palette: {
      primary: tokens.colors.primary,
      secondary: tokens.colors.secondary,
      background: tokens.colors.background,
      divider: tokens.colors.divider,
      text: tokens.colors.text,
      success: tokens.colors.success,
      warning: tokens.colors.warning,
      error: tokens.colors.error,
      info: tokens.colors.info,
    },

    typography: {
      fontFamily: tokens.typography.fontFamily.body,
      h1: {
        fontFamily: tokens.typography.fontFamily.heading,
        fontSize: tokens.typography.fontSize["4xl"],
        fontWeight: tokens.typography.fontWeight.bold,
        lineHeight: tokens.typography.lineHeight.tight,
      },
      h2: {
        fontFamily: tokens.typography.fontFamily.heading,
        fontSize: tokens.typography.fontSize["3xl"],
        fontWeight: tokens.typography.fontWeight.bold,
      },
      h3: {
        fontFamily: tokens.typography.fontFamily.heading,
        fontSize: tokens.typography.fontSize["2xl"],
        fontWeight: tokens.typography.fontWeight.semibold,
      },
      h4: {
        fontFamily: tokens.typography.fontFamily.heading,
        // Responsive page title: 1.25rem mobile, 1.5rem tablet, 1.75rem desktop
        fontSize: tokens.typography.fontSize.xl,
        fontWeight: tokens.typography.fontWeight.semibold,
        "@media (min-width: 600px)": {
          fontSize: tokens.typography.fontSize["2xl"],
        },
        "@media (min-width: 900px)": {
          fontSize: "1.75rem",
        },
      },
      h5: {
        fontFamily: tokens.typography.fontFamily.heading,
        // Responsive subheading: 1.125rem mobile, 1.25rem tablet, 1.5rem desktop
        fontSize: tokens.typography.fontSize.lg,
        fontWeight: tokens.typography.fontWeight.semibold,
        "@media (min-width: 600px)": {
          fontSize: tokens.typography.fontSize.xl,
        },
        "@media (min-width: 900px)": {
          fontSize: tokens.typography.fontSize["2xl"],
        },
      },
      h6: {
        fontFamily: tokens.typography.fontFamily.heading,
        fontSize: tokens.typography.fontSize.md,
        fontWeight: tokens.typography.fontWeight.semibold,
      },
      body1: {
        fontSize: tokens.typography.fontSize.md,
        lineHeight: tokens.typography.lineHeight.normal,
      },
      body2: {
        fontSize: tokens.typography.fontSize.sm,
        lineHeight: tokens.typography.lineHeight.normal,
      },
    },

    spacing: (factor: number) => tokens.spacing.md * factor,

    shape: {
      borderRadius: tokens.borderRadius.md,
    },

    components: {
      // Global focus-visible styles for accessibility (WCAG 2.4.7)
      MuiButtonBase: {
        styleOverrides: {
          root: {
            "&:focus-visible": {
              outline: `2px solid ${tokens.colors.primary.main}`,
              outlineOffset: 2,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: tokens.borderRadius.md,
            textTransform: "none",
            fontWeight: tokens.typography.fontWeight.medium,
            // Mobile touch target: 48px minimum (WCAG 2.5.5 AAA)
            minHeight: 48,
            "@media (min-width: 900px)": {
              minHeight: 40,
            },
          },
          sizeLarge: {
            padding: `${tokens.spacing.sm}px ${tokens.spacing.lg}px`,
          },
          sizeSmall: {
            minHeight: 40,
            "@media (min-width: 900px)": {
              minHeight: 32,
            },
          },
        },
        defaultProps: {
          disableElevation: true,
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            // 48px touch target on mobile (24px icon + 24px padding)
            padding: 12,
            "@media (min-width: 900px)": {
              padding: 8,
            },
          },
          sizeSmall: {
            padding: 8,
            "@media (min-width: 900px)": {
              padding: 4,
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            // Mobile touch target: 48px minimum
            minHeight: 48,
            paddingTop: 8,
            paddingBottom: 8,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: tokens.borderRadius.lg,
            boxShadow: tokens.shadows.md,
            backgroundColor: tokens.colors.background.paper,
            // Responsive padding: 16px on mobile, 24px on desktop
            // Can be overridden with sx prop where needed
            padding: 16,
            "@media (min-width: 900px)": {
              padding: 24,
            },
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          variant: "outlined",
          size: "small",
        },
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: tokens.borderRadius.md,
            },
            // Mobile touch target: 48px minimum
            "& .MuiInputBase-root": {
              minHeight: 48,
              "@media (min-width: 900px)": {
                minHeight: 40,
              },
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            // Mobile touch target: 48px minimum
            minHeight: 48,
            "@media (min-width: 900px)": {
              minHeight: 40,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            // Ensure adequate touch target for checkboxes
            padding: 12,
            "@media (min-width: 900px)": {
              padding: 9,
            },
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            // Ensure adequate touch target for radio buttons
            padding: 12,
            "@media (min-width: 900px)": {
              padding: 9,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: tokens.typography.fontWeight.semibold,
            backgroundColor: tokens.colors.primary.main,
            color: tokens.colors.primary.contrastText,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            "&:nth-of-type(even)": {
              backgroundColor: tokens.colors.background.subtle,
            },
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            backgroundColor: tokens.colors.secondary.main,
            height: 3,
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: tokens.typography.fontWeight.medium,
            "&.Mui-selected": {
              color: tokens.colors.primary.main,
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: tokens.borderRadius.sm,
          },
          colorPrimary: {
            backgroundColor: tokens.colors.primary.main,
            color: tokens.colors.primary.contrastText,
          },
          colorSecondary: {
            backgroundColor: tokens.colors.secondary.main,
            color: tokens.colors.secondary.contrastText,
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.colors.secondary.main,
            color: tokens.colors.secondary.contrastText,
          },
        },
      },
      MuiDivider: {
        styleOverrides: {
          root: {
            borderColor: tokens.colors.divider,
          },
        },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            borderBottom: "none",
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${tokens.colors.divider}`,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            // Mobile: edge-to-edge with no margin, full height
            margin: 0,
            maxHeight: "100%",
            // Desktop: 32px margin, constrained height
            "@media (min-width: 900px)": {
              margin: 32,
              maxHeight: "calc(100% - 64px)",
            },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            // Sticky actions for long forms on mobile
            padding: 16,
            // Safe area padding for notched devices
            paddingBottom: "max(16px, env(safe-area-inset-bottom, 16px))",
          },
        },
      },
    },
  });
}
